import { memo } from 'react'
import { useCreativeAssets } from '@/pages/campaigns/creatives/hooks'
import { TrashIcon } from '@/components/icons/TrashIcon.jsx'
import cn from 'classnames'
import ResponsiveImage from '@/components/mediaBuying/ResponsiveImage'
import { HoverableMediaPreview } from '@/components/ui/hoverable-media-preview'

// Thumbnail of a creative's first asset; with `hoverPreview` the full-size image or video opens on hover,
// matching the My Creatives page. `previewContent` replaces that plain media with custom content, such as the ad mockup.
const CreativeThumbnail = memo(
    ({ creative, size = 40, onRemove, showDeleteIcon = true, hoverPreview = false, previewContent = undefined }) => {
        // Fetch assets for this creative
        const { assetData, croppedImageUrl, loading, error } = useCreativeAssets(
            creative.id,
            creative.format?.toLowerCase() || 'singleImage',
        )

        // Use the same logic as CreativeCard to get the image URL
        const imageUrl = croppedImageUrl || assetData?.url

        // If loading, show placeholder
        if (loading) {
            return (
                <div
                    className="tw:bg-gray-700 tw:animate-pulse"
                    style={{ width: `${size}px`, height: `${size}px`, borderRadius: '2px' }}
                />
            )
        }

        // If error or no image, show placeholder
        if (error || !imageUrl) {
            return (
                <div
                    className="tw:bg-gray-700"
                    style={{ width: `${size}px`, height: `${size}px`, borderRadius: '2px' }}
                />
            )
        }

        const isVideo = assetData.type?.toLowerCase() === 'video'

        const thumbnail = (
            <div className="tw:relative tw:group/image" data-testid="creative-thumbnail">
                {isVideo ? (
                    <ResponsiveImage
                        images={assetData.thumbnails}
                        alt={creative.name || 'Video thumbnail'}
                        className="tw:object-cover"
                        style={{ width: `${size}px`, height: `${size}px`, borderRadius: '2px' }}
                    />
                ) : (
                    <img
                        src={imageUrl}
                        alt={creative.name || 'Creative'}
                        className="tw:w-full tw:h-full tw:object-cover"
                        style={{ width: `${size}px`, height: `${size}px`, borderRadius: '2px' }}
                    />
                )}
                {showDeleteIcon && onRemove && (
                    <div
                        className={cn(
                            'tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:p-0 tw:bg-primary/80',
                            'tw:opacity-0 tw:group-hover/image:opacity-100 tw:flex tw:items-center tw:justify-center',
                        )}
                        style={{ width: `${size}px`, height: `${size}px` }}
                    >
                        <button className="tw:p-0" onClick={onRemove}>
                            <TrashIcon className="tw:size-6 tw:text-blue-300" />
                        </button>
                    </div>
                )}
            </div>
        )

        if (!hoverPreview) return thumbnail

        const aspectRatio = assetData.width && assetData.height ? assetData.width / assetData.height : undefined

        return (
            <HoverableMediaPreview
                src={assetData.url}
                mediaType={isVideo ? 'video' : 'image'}
                aspectRatio={previewContent ? undefined : aspectRatio}
                previewContent={previewContent}
                thumbnailClassName="tw:cursor-pointer"
            >
                {thumbnail}
            </HoverableMediaPreview>
        )
    },
)

CreativeThumbnail.displayName = 'CreativeThumbnail'

export default CreativeThumbnail
